import React, { useState, useEffect } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    ReferenceInput,
    SelectInput,
    required,
    email,
    minLength,
    Toolbar,
    SaveButton,
    useRecordContext,
    useDelete,
    useNotify,
    useRedirect,
    useGetIdentity,
    useTranslate,
    useRefresh,
    useDataProvider,
    usePermissions,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    IconButton,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import {
    Security,
    Person,
    Settings,
    Warning,
    ArrowBack,
    Delete,
    SecurityUpdate,
    Business,
    LockReset,
    ContentCopy,
    Check,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import {
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Divider,
} from '@mui/material';
import { apiClient } from '../../utils/apiClient';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const USER_KNOWN_FIELDS = new Set([
    'email', 'username', 'firstName', 'lastName', 'password',
    'phone', 'roleId', 'vendorId', 'isActive', 'language',
]);

const mapUserBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const userFieldLabelKey = (field: string): string =>
    USER_KNOWN_FIELDS.has(field)
        ? `resources.backend-users.fields.${field}`
        : field;

const TwoFactorManagementSection = () => {
    const record = useRecordContext();
    const { data: identity } = useGetIdentity();
    const notify = useNotify();
    const translate = useTranslate();
    const refresh = useRefresh();

    const isOwnAccount = identity?.id === record?.id;
    const has2FA = record?.totpEnabled;

    const handleDisable2FA = async () => {
        if (!record?.id) return;

        if (window.confirm(translate('resources.backend-users.twoFactorAuth.confirmDisable'))) {
            try {
                await apiClient.post(`/api/backend/users/${record.id}/2fa/disable`);
                notify(translate('resources.backend-users.twoFactorAuth.disabledSuccess'), { type: 'success' });
                refresh();
            } catch (error: any) {
                notify(translate('resources.backend-users.twoFactorAuth.disabledError'), { type: 'error' });
            }
        }
    };

    return (
        <div style={{ padding: '16px', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SecurityUpdate style={{ color: has2FA ? '#00C853' : '#757575' }} />
                    <span style={{ fontWeight: 500 }}>
                        {translate('resources.backend-users.edit.twoFactorAuth')}
                    </span>
                </div>
                <span style={{
                    backgroundColor: has2FA ? '#00C853' : '#757575',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '16px',
                    fontSize: '12px'
                }}>
                    {has2FA ? translate('resources.backend-users.status.active') : translate('resources.backend-users.status.inactive')}
                </span>
            </div>

            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                {isOwnAccount
                    ? translate('resources.backend-users.edit.twoFactorHelpText')
                    : has2FA
                        ? translate('resources.backend-users.edit.admin2faDisableHelp')
                        : translate('resources.backend-users.edit.admin2faEnableHelp')
                }
            </p>


            {!isOwnAccount && has2FA && (
                <button
                    onClick={handleDisable2FA}
                    style={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    {translate('resources.backend-users.actions.disable2FA')}
                </button>
            )}

            {isOwnAccount && (
                <Alert severity="info">
                    <Typography variant="body2">
                        {translate('resources.backend-users.edit.admin2faOwnAccountWarning')}
                    </Typography>
                </Alert>
            )}

            {!isOwnAccount && !has2FA && (
                <Alert severity="info">
                    <Typography variant="body2">
                        {translate('resources.backend-users.edit.admin2faUserNoTwoFactor')}
                    </Typography>
                </Alert>
            )}
        </div>
    );
};

// Same complexity rule as backend (lib/auth/validation.ts:43–46) so the admin
// gets immediate feedback before the request fires.
const PASSWORD_COMPLEXITY_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])./;

const validateCustomPassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'minLength';
    if (!PASSWORD_COMPLEXITY_REGEX.test(pwd)) return 'complexity';
    return null;
};

const ResetPasswordSection = () => {
    const record = useRecordContext();
    const { data: identity } = useGetIdentity();
    const { permissions } = usePermissions();
    const notify = useNotify();
    const translate = useTranslate();

    const isOwnAccount = identity?.id === record?.id;
    const canReset =
        Array.isArray(permissions) &&
        (permissions.includes('users:admin:all') || permissions.includes('*:*:*'));

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [mode, setMode] = useState<'random' | 'custom'>('random');
    const [customPassword, setCustomPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [resultMode, setResultMode] = useState<'random' | 'custom'>('random');
    const [tempPassword, setTempPassword] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!record) return null;
    if (!canReset) return null;

    const passwordError = mode === 'custom' ? validateCustomPassword(customPassword) : null;
    const confirmMismatch = mode === 'custom' && customPassword !== confirmPassword;
    const customInvalid =
        mode === 'custom' &&
        (customPassword.length === 0 || passwordError !== null || confirmMismatch);

    const resetDialogState = () => {
        setMode('random');
        setCustomPassword('');
        setConfirmPassword('');
        setShowPassword(false);
    };

    const handleOpenConfirm = () => {
        resetDialogState();
        setConfirmOpen(true);
    };

    const handleConfirmClose = () => {
        if (submitting) return;
        setConfirmOpen(false);
        resetDialogState();
    };

    const handleSubmit = async () => {
        if (mode === 'custom' && customInvalid) return;
        setSubmitting(true);
        try {
            const body = mode === 'custom' ? { password: customPassword } : {};
            const response: any = await apiClient.post(
                `/api/backend/users/${record.id}/password/reset`,
                body
            );
            const returnedMode: 'random' | 'custom' =
                response?.data?.mode === 'custom' ? 'custom' : 'random';
            setResultMode(returnedMode);
            if (returnedMode === 'random') {
                const password = response?.data?.temp_password;
                if (!password) throw new Error('No temp password returned');
                setTempPassword(password);
            } else {
                setTempPassword(null);
            }
            setConfirmOpen(false);
            setResultOpen(true);
            // NB: don't call resetDialogState() here — doing so during the
            // fade-out animation makes the radio briefly snap to "random" while
            // the dialog is still visible. Re-opening the dialog via the Reset
            // button calls handleOpenConfirm() which resets cleanly.
            //
            // Success feedback for custom mode is the result dialog itself
            // (success Alert) — no duplicate toast.
        } catch (err: any) {
            const apiError = err?.body || err;
            const fieldErrors = apiError?.errors;
            const rawMessage = err?.message || 'resources.backend-users.passwordReset.failed';
            const baseMessage = translate(rawMessage, { _: rawMessage });
            if (fieldErrors && typeof fieldErrors === 'object') {
                const details = Object.entries(fieldErrors)
                    .map(([k, v]) => `• ${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                    .join('\n');
                notify(`${baseMessage}\n${details}`, {
                    type: 'error',
                    multiLine: true,
                    autoHideDuration: 10000,
                });
            } else {
                notify(baseMessage, { type: 'error', autoHideDuration: 7000 });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async () => {
        if (!tempPassword) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(tempPassword);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = tempPassword;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error('[ResetPasswordSection] Copy failed:', err);
        }
    };

    const handleResultClose = () => {
        setResultOpen(false);
        // Discard the password from React state so reopening shows nothing.
        setTempPassword(null);
        setCopied(false);
    };

    // Reuse the same MUI Button style as the toolbar SaveButton
    // (rounded, no text-transform, fontWeight 600, generous padding) so the
    // affordance feels at home next to other admin actions.
    const buttonSx = {
        borderRadius: 2,
        textTransform: 'none' as const,
        fontWeight: 600,
        px: 3,
        py: 1.25,
    };

    const button = (
        <Button
            onClick={handleOpenConfirm}
            disabled={isOwnAccount}
            variant="contained"
            color="warning"
            startIcon={<LockReset />}
            sx={buttonSx}
        >
            {translate('resources.backend-users.actions.resetPassword', { _: 'Reset password' })}
        </Button>
    );

    return (
        <div style={{ padding: '16px', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <LockReset style={{ color: '#ff9800' }} />
                <span style={{ fontWeight: 500 }}>
                    {translate('resources.backend-users.passwordReset.sectionTitle', { _: 'Password' })}
                </span>
            </div>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                {isOwnAccount
                    ? translate('resources.backend-users.passwordReset.ownAccountHelp', {
                          _: 'Use the change-password section in your profile to change your own password.',
                      })
                    : translate('resources.backend-users.passwordReset.adminHelp', {
                          _: 'Reset this user\'s password. You can either generate a strong random one or set a specific password yourself.',
                      })}
            </p>

            {isOwnAccount ? (
                <Tooltip
                    title={translate('resources.backend-users.passwordReset.ownAccountTooltip', {
                        _: 'Use the change-password section in My Profile to change your own password.',
                    })}
                >
                    <span>{button}</span>
                </Tooltip>
            ) : (
                button
            )}

            <Dialog open={confirmOpen} onClose={handleConfirmClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {translate('resources.backend-users.passwordReset.confirmTitle', {
                        _: "Reset this user's password?",
                    })}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                            {translate('resources.backend-users.passwordReset.confirmWarningTitle', {
                                _: 'This action will:',
                            })}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            <li>
                                <Typography variant="body2">
                                    {translate('resources.backend-users.passwordReset.confirmBullet1', {
                                        _: "Replace the user's password",
                                    })}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    {translate('resources.backend-users.passwordReset.confirmBullet2', {
                                        _: 'Sign the user out of all active sessions',
                                    })}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    {translate('resources.backend-users.passwordReset.confirmBullet3', {
                                        _: 'Clear any account lock from failed login attempts',
                                    })}
                                </Typography>
                            </li>
                        </Box>
                    </Alert>

                    <FormControl sx={{ mb: 2 }}>
                        <FormLabel sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                            {translate('resources.backend-users.passwordReset.modeLabel', {
                                _: 'New password',
                            })}
                        </FormLabel>
                        <RadioGroup
                            value={mode}
                            onChange={(e) => setMode(e.target.value as 'random' | 'custom')}
                        >
                            <FormControlLabel
                                value="random"
                                control={<Radio size="small" />}
                                label={translate('resources.backend-users.passwordReset.modeRandom', {
                                    _: 'Generate a strong random password (shown to you once)',
                                })}
                                disabled={submitting}
                            />
                            <FormControlLabel
                                value="custom"
                                control={<Radio size="small" />}
                                label={translate('resources.backend-users.passwordReset.modeCustom', {
                                    _: 'Set a specific password',
                                })}
                                disabled={submitting}
                            />
                        </RadioGroup>
                    </FormControl>

                    {mode === 'custom' && (
                        <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label={translate('resources.backend-users.passwordReset.passwordLabel', {
                                    _: 'New password',
                                })}
                                type={showPassword ? 'text' : 'password'}
                                value={customPassword}
                                onChange={(e) => setCustomPassword(e.target.value)}
                                error={customPassword.length > 0 && passwordError !== null}
                                helperText={
                                    customPassword.length === 0
                                        ? translate('resources.backend-users.passwordReset.passwordHint', {
                                              _: 'Min 8 characters with uppercase, lowercase, digit, and at least one special character',
                                          })
                                        : passwordError === 'minLength'
                                        ? translate('resources.backend-users.passwordReset.passwordTooShort', {
                                              _: 'Must be at least 8 characters',
                                          })
                                        : passwordError === 'complexity'
                                        ? translate('resources.backend-users.passwordReset.passwordWeak', {
                                              _: 'Must include uppercase, lowercase, digit, and at least one special character',
                                          })
                                        : ' '
                                }
                                disabled={submitting}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword((v) => !v)}
                                                edge="end"
                                                size="small"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label={translate('resources.backend-users.passwordReset.confirmLabel', {
                                    _: 'Confirm new password',
                                })}
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                error={confirmPassword.length > 0 && confirmMismatch}
                                helperText={
                                    confirmPassword.length > 0 && confirmMismatch
                                        ? translate('resources.backend-users.passwordReset.confirmMismatch', {
                                              _: 'Passwords do not match',
                                          })
                                        : ' '
                                }
                                disabled={submitting}
                            />
                        </Box>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="body2" color="text.secondary">
                        {mode === 'random'
                            ? translate('resources.backend-users.passwordReset.confirmBodyRandom', {
                                  _: 'You will see the temporary password ONCE. Copy it and deliver it to the user through a secure channel.',
                              })
                            : translate('resources.backend-users.passwordReset.confirmBodyCustom', {
                                  _: 'The user will have to log in with this password. Make sure to share it through a secure channel.',
                              })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose} disabled={submitting} sx={buttonSx}>
                        {translate('resources.backend-users.passwordReset.cancel', { _: 'Cancel' })}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="warning"
                        startIcon={<LockReset />}
                        disabled={submitting || customInvalid}
                        sx={buttonSx}
                    >
                        {translate('resources.backend-users.passwordReset.confirm', {
                            _: 'Reset password',
                        })}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={resultOpen} onClose={handleResultClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {resultMode === 'random'
                        ? translate('resources.backend-users.passwordReset.resultTitle', {
                              _: 'Temporary password',
                          })
                        : translate('resources.backend-users.passwordReset.resultTitleCustom', {
                              _: 'Password reset',
                          })}
                </DialogTitle>
                <DialogContent>
                    {resultMode === 'random' ? (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {translate('resources.backend-users.passwordReset.resultWarning', {
                                    _: 'This password is shown only once. Copy it now and deliver it to the user through a secure channel.',
                                })}
                            </Alert>
                            <TextField
                                fullWidth
                                value={tempPassword || ''}
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily:
                                            'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        fontSize: '1.05rem',
                                        letterSpacing: '0.04em',
                                    },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip
                                                title={
                                                    copied
                                                        ? translate(
                                                              'resources.backend-users.passwordReset.copied',
                                                              { _: 'Copied!' }
                                                          )
                                                        : translate(
                                                              'resources.backend-users.passwordReset.copy',
                                                              { _: 'Copy' }
                                                          )
                                                }
                                            >
                                                <IconButton
                                                    onClick={handleCopy}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {copied ? (
                                                        <Check sx={{ color: 'success.main' }} />
                                                    ) : (
                                                        <ContentCopy />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </>
                    ) : (
                        <Alert severity="success">
                            {translate('resources.backend-users.passwordReset.customSuccess', {
                                _: 'Password set. The user has been signed out and must use the new password to log in.',
                            })}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleResultClose} variant="contained" sx={buttonSx}>
                        {translate('resources.backend-users.passwordReset.done', { _: 'Done' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const DeleteUserDialog = ({
    open,
    onClose,
    onConfirm,
    username
}: {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    username: string;
}) => {
    const [confirmText, setConfirmText] = useState('');
    const translate = useTranslate();

    const handleConfirm = () => {
        if (confirmText === username) {
            onConfirm();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning sx={{ color: '#f44336' }} />
                    <span>{translate('resources.backend-users.edit.deleteConfirmTitle')}</span>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Alert severity="warning">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {translate('resources.backend-users.edit.deleteConfirmWarning')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {translate('resources.backend-users.edit.deleteConfirmMessage')} <strong>{username}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {translate('resources.backend-users.edit.deleteConfirmInfo')}
                        </Typography>
                    </Alert>
                </Box>

                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {translate('resources.backend-users.edit.deleteConfirmPrompt')} <strong>{username}</strong> {translate('resources.backend-users.edit.deleteConfirmPromptBelow')}
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={translate('resources.backend-users.edit.deleteConfirmTypePlaceholder', { username })}
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        error={confirmText !== '' && confirmText !== username}
                        helperText={confirmText !== '' && confirmText !== username ? translate('resources.backend-users.edit.usernameDoesNotMatch') : ''}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} color="inherit">
                    {translate('ra.action.cancel')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="error"
                    disabled={confirmText !== username}
                >
                    {translate('resources.backend-users.edit.deleteButton')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DeleteUserSection = () => {
    const record = useRecordContext();
    const { data: identity } = useGetIdentity();
    const [deleteUser] = useDelete();
    const notify = useNotify();
    const redirect = useRedirect();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const translate = useTranslate();

    const isOwnAccount = identity?.id === record?.id;

    const handleDelete = async () => {
        if (!record) return;
        try {
            await deleteUser('backend-users', { id: record.id, previousData: record });
            notify(translate('resources.backend-users.notifications.deleteSuccess'), { type: 'success' });
            redirect('list', '/backend-users');
        } catch (error) {
            notify(translate('resources.backend-users.notifications.deleteError'), { type: 'error' });
        }
    };

    if (!record) return null;

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 0 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {isOwnAccount
                            ? translate('resources.backend-users.edit.cannotDeleteOwnAccount')
                            : translate('resources.backend-users.edit.deleteAccountDescription')}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isOwnAccount}
                    sx={{
                        flexShrink: 0,
                        alignSelf: { xs: 'flex-end', sm: 'auto' },
                        borderColor: isOwnAccount ? '#BDBDBD' : '#F44336',
                        color: isOwnAccount ? '#BDBDBD' : '#F44336',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                        '&:hover': {
                            borderColor: isOwnAccount ? '#BDBDBD' : '#D32F2F',
                            backgroundColor: isOwnAccount ? 'transparent' : 'rgba(244,67,54,0.08)',
                        },
                        '&.Mui-disabled': {
                            borderColor: '#BDBDBD',
                            color: '#BDBDBD',
                        },
                    }}
                    title={isOwnAccount
                        ? translate('resources.backend-users.edit.cannotDeleteOwnAccount')
                        : translate('common.delete')}
                >
                    {translate('common.delete')}
                </Button>
            </Box>

            <DeleteUserDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={() => {
                    setDeleteDialogOpen(false);
                    handleDelete();
                }}
                username={record?.username || ''}
            />
        </>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/backend-users')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.backend-users.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.backend-users.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {record?.username} • {record?.email}
                </Typography>
            </Box>
        </Box>
    );
};

const VendorFields = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const { data: identity } = useGetIdentity();
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [vendorRole, setVendorRole] = useState<string | null>(null);

    // Vendor-scope: restrict the vendor dropdown + make it read-only for
    // vendor-scoped actors. Changing a backend admin user's vendor assignment
    // is a privilege boundary — only super-admin should move users between
    // tenants. Backend users-pg.ts should also gate this server-side.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;

    useEffect(() => {
        if (record) {
            if (record.vendors && record.vendors.length > 0) {
                setVendorId(record.vendors[0].vendorId);
                setVendorRole(record.vendors[0].role || null);
            } else if (record.vendorId) {
                setVendorId(record.vendorId);
            }
        }
    }, [record]);

    return (
        <>
            <ReferenceInput
                source="vendorId"
                reference="vendors"
                emptyText="None"
                emptyValue={null}
                filter={isVendorScoped ? { id: actorVendorIds } : undefined}
            >
                <SelectInput
                    optionText="name"
                    fullWidth
                    readOnly={isVendorScoped}
                    helperText={translate('resources.backend-users.edit.vendorHelperText')}
                    defaultValue={vendorId}
                />
            </ReferenceInput>

            {/*
              Vendor role picker. Backend now requires an explicit `vendorRole`
              whenever `vendorId` is set on update (see updateUserSchema's
              superRefine). Pre-loaded from the existing assignment so the
              admin's role is preserved unless they actively change it — no
              more silent server-side default to 'operator' that could clobber
              a 'manager' assignment on an unrelated edit.
            */}
            <SelectInput
                source="vendorRole"
                label={translate('resources.backend-users.fields.vendorRole', { _: 'Vendor role' })}
                choices={[
                    { id: 'viewer', name: translate('resources.backend-users.vendorRoles.viewer', { _: 'Viewer' }) },
                    { id: 'operator', name: translate('resources.backend-users.vendorRoles.operator', { _: 'Operator' }) },
                    { id: 'manager', name: translate('resources.backend-users.vendorRoles.manager', { _: 'Manager' }) },
                    { id: 'admin', name: translate('resources.backend-users.vendorRoles.admin', { _: 'Admin' }) },
                ]}
                defaultValue={vendorRole}
                fullWidth
                helperText={translate('resources.backend-users.edit.vendorRoleHelperText', { _: 'Permission level within the assigned vendor' })}
            />

            <Alert severity="info">
                <Typography variant="caption">
                    {translate('resources.backend-users.edit.vendorAssignmentInfo')}
                </Typography>
            </Alert>
        </>
    );
};

export const UserEdit = () => {
    const validateEmail = [required(), email()];
    const validateUsername = [required(), minLength(3)];
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Edit
            title={false}
            component="div"
            mutationMode="pessimistic"
            redirect={false}
            actions={false}
            transform={(data: any) => {
                // Strip server-only readback fields. Backend rejects unknown
                // keys on `data` indirectly via the typed picks below.
                const { vendors, vendorCount, ...rest } = data;
                // Strip vendorRole when no vendorId is set — backend rejects
                // role-without-vendor via superRefine. Without this, an admin
                // editing a user with no vendor assignment would 400 because
                // the role default lingers in the form state.
                if (!rest.vendorId) {
                    delete rest.vendorRole;
                }
                return rest;
            }}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.backend-users.notifications.updateSuccess', {
                        type: 'success',
                        autoHideDuration: 3000
                    });
                    redirect('list', 'backend-users');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapUserBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(userFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    padding: { xs: 0, sm: 'inherit' },
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Person sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.edit.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                    <TextInput
                                        source="firstName"
                                        fullWidth
                                    />
                                    <TextInput
                                        source="lastName"
                                        fullWidth
                                    />
                                </Box>

                                <TextInput
                                    source="username"
                                    validate={validateUsername}
                                    fullWidth
                                />

                                <TextInput
                                    source="email"
                                    validate={validateEmail}
                                    fullWidth
                                />

                                <ReferenceInput source="roleId" reference="roles">
                                    <SelectInput
                                        optionText="name"
                                        fullWidth
                                        validate={required()}
                                    />
                                </ReferenceInput>

                                <SelectInput
                                    source="language"
                                    choices={[
                                        { id: 'en', name: translate('language.en') },
                                        { id: 'zh-TW', name: translate('language.zh-TW') },
                                    ]}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Vendor Assignment Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Business sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.edit.vendorAssignment')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <VendorFields />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Security Settings Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Security sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.edit.securitySettings')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 3 }}>
                                <TwoFactorManagementSection />
                                <ResetPasswordSection />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Account Status Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.edit.accountStatus')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.backend-users.edit.activeAccount')}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#00C853',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#00C853',
                                        },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Delete Account Section */}
                    <Card sx={{ mt: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Delete sx={{ mr: 2, color: '#F44336' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.edit.deleteAccount')}
                                </Typography>
                            </Box>

                            <DeleteUserSection />
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};